/**
 * Cloud implementation of the shared MapLayoutApi. Node-scoped: binds the currently
 * selected node id, like the groups/models/heatmap adapters.
 *
 * The floor plan image comes back INLINE as base64 on the layout detail — the tunnel
 * carries JSON, so there is no binary route for the browser to hit. This turns it into a
 * `data:` URL for `<img src>` (the same shape `AttendanceApi.eventImageDataUrl` uses) and
 * caches it by layout id + updated_at, so flipping between layouts doesn't re-ship a
 * megabyte over the tunnel for an image already on the page.
 */
import type {
  MapLayoutApi,
  MapLayoutDetail,
  MapLayoutInput,
  MapLayoutSummary,
  MapPlacedCamera,
  MapPlacement,
} from '@trinetra/shared';
import {
  createNodeMapLayout,
  deleteNodeMapLayout,
  getNodeMapLayout,
  getNodeMapLayouts,
  getNodeMapPlacedCameras,
  setNodeMapPlacements,
  updateNodeMapLayout,
} from '../services/api.service';
import { selectedNodeId as nodeId } from './node';

/** Node id or '' — the shared page renders an empty state when no node is picked. */
const nid = () => nodeId() || '';

/**
 * Re-throw a proxy failure with the NODE's message. The cloud BE relays the local status
 * and message verbatim, so a placement clash arrives as "CAM-12 is already placed on
 * Floor 1" — which axios would otherwise flatten to "Request failed with status code 409".
 */
function rethrow(e: any): never {
  const body = e?.response?.data;
  throw new Error(body?.message || body?.error || e?.message || 'Request failed');
}

/** Layout id + updated_at → data URL. Bounded implicitly: one entry per layout version. */
const imageCache = new Map<string, string>();

export const cloudMapLayoutApi: MapLayoutApi = {
  async list(): Promise<MapLayoutSummary[]> {
    const id = nid();
    if (!id) return [];
    const rows = await getNodeMapLayouts(id).catch(rethrow);
    return Array.isArray(rows) ? rows : [];
  },

  async get(layoutId: string): Promise<MapLayoutDetail> {
    const id = nid();
    if (!id) throw new Error('Select a node first');
    const detail = await getNodeMapLayout(id, layoutId).catch(rethrow);
    // Cache the image here rather than in imageUrl(): this is the only response that
    // carries the bytes, and the page calls imageUrl() right after with the same layout.
    if (detail?.image_data && detail?.image_mime) {
      imageCache.set(
        `${layoutId}:${detail.updated_at ?? ''}`,
        `data:${detail.image_mime};base64,${detail.image_data}`,
      );
    }
    return detail as MapLayoutDetail;
  },

  async imageUrl(layout: MapLayoutSummary): Promise<string> {
    const key = `${layout.id}:${layout.updated_at ?? ''}`;
    const hit = imageCache.get(key);
    if (hit) return hit;
    // The page normally calls get() first, which fills the cache. This is the fallback for
    // a summary handed over without a detail read. Reached by NAME, not `this`: callers
    // pass this method around as a plain function (the layout dialog's preview resolver),
    // where `this` would be undefined.
    const detail: any = await cloudMapLayoutApi.get(layout.id);
    if (!detail?.image_data) throw new Error('That layout has no image');
    return `data:${detail.image_mime};base64,${detail.image_data}`;
  },

  create(input: MapLayoutInput & { is_default?: boolean }): Promise<MapLayoutSummary> {
    const id = nid();
    if (!id) throw new Error('Select a node first');
    return createNodeMapLayout(id, {
      name: input.name,
      description: input.description || undefined,
      image_mime: input.imageMime,
      image_data: input.imageData,
      image_width: input.imageWidth,
      image_height: input.imageHeight,
      is_default: input.is_default,
    }).catch(rethrow);
  },

  update(
    layoutId: string,
    patch: Partial<MapLayoutInput> & { is_default?: boolean },
  ): Promise<MapLayoutSummary> {
    const id = nid();
    if (!id) throw new Error('Select a node first');
    return updateNodeMapLayout(id, layoutId, {
      ...(patch.name !== undefined ? { name: patch.name } : {}),
      ...(patch.description !== undefined ? { description: patch.description } : {}),
      ...(patch.imageData !== undefined
        ? {
            image_mime: patch.imageMime,
            image_data: patch.imageData,
            image_width: patch.imageWidth,
            image_height: patch.imageHeight,
          }
        : {}),
      ...(patch.is_default !== undefined ? { is_default: patch.is_default } : {}),
    }).catch(rethrow);
  },

  async delete(layoutId: string): Promise<void> {
    const id = nid();
    if (!id) return;
    await deleteNodeMapLayout(id, layoutId).catch(rethrow);
    // Drop every cached version of that layout's image.
    for (const key of [...imageCache.keys()]) if (key.startsWith(`${layoutId}:`)) imageCache.delete(key);
  },

  async placedCameras(): Promise<Record<string, MapPlacedCamera>> {
    const id = nid();
    if (!id) return {};
    const map = await getNodeMapPlacedCameras(id).catch(() => ({}));
    return (map && typeof map === 'object' ? map : {}) as Record<string, MapPlacedCamera>;
  },

  setPlacements(layoutId: string, placements: MapPlacement[]): Promise<MapLayoutDetail> {
    const id = nid();
    if (!id) throw new Error('Select a node first');
    return setNodeMapPlacements(id, layoutId, placements).catch(rethrow) as Promise<MapLayoutDetail>;
  },
};
